import { getAllQuestionnaireAnswers } from "@medplum/core";
import {
  Coding,
  Encounter,
  QuestionnaireResponse,
  QuestionnaireResponseItemAnswer,
} from "@medplum/fhirtypes";

import { isNumber } from "lodash";
import { z } from "zod";

import { getImperialUnitFields } from "@/encounters/components/VitalSigns/utils/vitals-unit-system";
import { TemperatureUnitChoices } from "@/encounters/constants/vital-sign";
import { getEncounterServiceDateDetailsFromEncounter } from "@/encounters/utils/encounter-service-date-form";
import { Systems } from "@/fhir/enums/systems";
import { WeightUnitChoices } from "@/fhir/weight";
import { parseDateTimeFormat } from "@/utils/datetime";
import { getQuestionnaireAnswerValue } from "@/utils/questionnaires";
import {
  addRequiredTogetherIssues,
  zCoding,
  zCodingWithText,
  zDateInputNullish,
  zLOINCCode,
  zNativeEnumOptional,
  zNumberOptional,
} from "@/utils/zod";

import {
  DEFAULT_ENCOUNTER_SERVICE_DATE_DETAILS,
  EncounterServiceDateDetailsFields,
} from "./encounter-service-date-details";
import {
  EncounterAmendmentFormValues,
  EncounterAmendmentFormValuesRequired,
} from "./shared/EncounterAmendmentFormValues";

export const FundalHeightUnitChoices = {
  "Centimeters from pubis": "cm",
  "Fingers above pubis": "F>P",
  "Fingers below umbilicus": "F<U",
} as const;

export const FetalPresentationChoices = {
  Unknown: "unknown",
  Breech: "breech",
  Vertex: "vertex",
  Transverse: "transverse",
} as const;

export const BodyHeightUnitChoices = {
  // "Feet and Inches" is not a valid FHIR code, but it's used in the UI.
  // The value is converted to inches before saving it.
  "Feet and Inches": "in",
  Centimeters: "cm",
} as const;

export const VitalSignsForm = z
  .object({
    encounterVitalSignsWeight: zNumberOptional({ positive: true }),
    encounterVitalSignsWeightUnit: zNativeEnumOptional(WeightUnitChoices),
    encounterVitalSignsHeightUnit: zNativeEnumOptional(BodyHeightUnitChoices).optional(),
    encounterVitalSignsHeightFeet: zNumberOptional({ min: 0, max: 9 }),
    encounterVitalSignsHeightInches: zNumberOptional({ min: 0, max: 11.99 }),
    encounterVitalSignsHeightCm: zNumberOptional({ positive: true }),
    encounterVitalSignsSystolicBP: zNumberOptional({ positive: true }),
    encounterVitalSignsDiastolicBP: zNumberOptional({ positive: true }),
    encounterVitalSignsHeartRate: zNumberOptional({ positive: true }),
    encounterVitalSignsTemperature: zNumberOptional({ positive: true }),
    encounterVitalSignsTemperatureUnit: zNativeEnumOptional(TemperatureUnitChoices),
    encounterVitalSignsTemperatureMethod: zLOINCCode().optional(),
    encounterVitalSignsRespiratoryRate: zNumberOptional({ positive: true }),
    encounterVitalSignsVitalsComments: z.string().optional(),
  })
  .superRefine((form, ctx) => {
    if (isNumber(form.encounterVitalSignsWeight) && !form.encounterVitalSignsWeightUnit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["encounterVitalSignsWeightUnit"],
        message: "Weight unit is required",
      });
    }

    if (
      !isNumber(form.encounterVitalSignsWeight) &&
      form.encounterVitalSignsWeightUnit &&
      form.encounterVitalSignsWeightUnit !== WeightUnitChoices.lbs
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["encounterVitalSignsWeight"],
        message: "Weight is required",
      });
    }

    if (
      (isNumber(form.encounterVitalSignsHeightInches) ||
        isNumber(form.encounterVitalSignsHeightFeet) ||
        isNumber(form.encounterVitalSignsHeightCm)) &&
      !form.encounterVitalSignsHeightUnit
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["encounterVitalSignsHeightUnit"],
        message: "Height unit is required",
      });
    }

    if (isNumber(form.encounterVitalSignsTemperature) && !form.encounterVitalSignsTemperatureUnit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["encounterVitalSignsTemperatureUnit"],
        message: "Temperature scale is required",
      });
    }

    if (
      !isNumber(form.encounterVitalSignsTemperature) &&
      (form.encounterVitalSignsTemperatureUnit || form.encounterVitalSignsTemperatureMethod) &&
      form.encounterVitalSignsTemperatureUnit !== TemperatureUnitChoices.F
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["encounterVitalSignsTemperature"],
        message: "Temperature is required",
      });
    }

    if (
      isNumber(form.encounterVitalSignsTemperature) &&
      form.encounterVitalSignsTemperatureUnit &&
      !form.encounterVitalSignsTemperatureMethod
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["encounterVitalSignsTemperatureMethod"],
        message: "Temperature method is required",
      });
    }
    if (isNumber(form.encounterVitalSignsSystolicBP) && !form.encounterVitalSignsDiastolicBP) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["encounterVitalSignsDiastolicBP"],
        message: "Diastolic blood pressure is required",
      });
    }
    if (isNumber(form.encounterVitalSignsDiastolicBP) && !form.encounterVitalSignsSystolicBP) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["encounterVitalSignsSystolicBP"],
        message: "Systolic blood pressure is required",
      });
    }
    return true;
  });

export const PrenatalFlowsheetForm = z
  .object({
    encounterFlowSheetWeight: zNumberOptional({ positive: true }),
    encounterFlowSheetWeightUnit: zNativeEnumOptional(WeightUnitChoices),
    encounterFlowSheetSystolicBP: zNumberOptional({ positive: true }),
    encounterFlowSheetDiastolicBP: zNumberOptional({ positive: true }),
    encounterFlowSheetHeartRate: zNumberOptional({ positive: true }),
    encounterFlowSheetFundalHeight: zNumberOptional({ positive: true }),
    encounterFlowSheetFundalHeightUnit: zNativeEnumOptional(FundalHeightUnitChoices),
    encounterFlowSheetFetalPresentation: zNativeEnumOptional(FetalPresentationChoices),
    encounterFlowSheetFetalHeartRate: zNumberOptional({ positive: true }),
    encounterFlowSheetFetalMovement: zCoding().optional(),
    encounterFlowSheetUrineProtein: zCoding().optional(),
    encounterFlowSheetUrineGlucose: zCoding().optional(),
    encounterFlowSheetEdema: zCoding().optional(),
    encounterFlowSheetNotes: z.string().optional(),
    encounterFlowSheetDate: zDateInputNullish(),
  })
  .superRefine((form, ctx) => {
    addRequiredTogetherIssues(form, ctx, [
      "encounterFlowSheetFundalHeight",
      "encounterFlowSheetFundalHeightUnit",
    ]);
    addRequiredTogetherIssues(form, ctx, [
      "encounterFlowSheetWeight",
      "encounterFlowSheetWeightUnit",
    ]);
    return true;
  });

export const EncounterDxPlanFormSchema = z.object({
  encounterDiagnosisCode: zCoding().refine((code) => code.code !== "", {
    message: "Diagnosis code is required",
  }),
  encounterDiagnosisAddToProblemList: z.boolean().optional(),
  encounterDiagnosisNotes: z.string().optional(),
});

export const EncounterAnswersForm = z.object({
  // TODO: Refactor to use only one field name and keep code consistent.
  // "notes" is used in GynecologicalEncounter, NewbornIPPEncounter, NewbornPostpartumEncounter and PostpartumEncounter.
  notes: z.string().optional(),
  // "encounterNotes" is used in TransferOfCareEncounter.
  encounterNotes: z.string().optional(),
  // "encounterNote" is used in AmendEncounter, EncounterBase, EncounterLabor and PrimaryCareEncounter.
  encounterNote: z.string().optional(),
  encounterClientDiagnoses: z.array(EncounterDxPlanFormSchema).optional(),
  encounterClientSummary: z.string().optional(),
  encounterFlowSheet: PrenatalFlowsheetForm.optional(),
  encounterVitalSigns: VitalSignsForm.optional(),
});

export const EncounterDetailsForm = z
  .object({
    encounterTemplate: zCoding().refine((template) => template.code !== "", {
      message: "Template is required",
    }),
    encounterType: zCodingWithText().refine((type) => type.code !== "", {
      message: "Type is required",
    }),
    encounterLocation: zLOINCCode().refine((location) => location.code !== "", {
      message: "Location is required",
    }),
  })
  .merge(EncounterServiceDateDetailsFields);

export const EncounterAmendmentFormSchema = EncounterAnswersForm.extend({
  encounterAmendmentInfo: EncounterAmendmentFormValues.optional(),
  encounterDetails: EncounterDetailsForm.optional(),
});

// Required schema for amendment mode validation (requires amendment reason)
export const EncounterAmendmentFormSchemaRequired = EncounterAnswersForm.extend({
  encounterAmendmentInfo: EncounterAmendmentFormValuesRequired,
  encounterDetails: EncounterDetailsForm.optional(),
});

export type EncounterAnswersFormType = z.infer<typeof EncounterAnswersForm>;
export type EncounterAmendmentFormType = z.infer<typeof EncounterAmendmentFormSchema>;
export type VitalSignsFormType = z.infer<typeof VitalSignsForm>;
export type PrenatalFlowsheetFormType = z.infer<typeof PrenatalFlowsheetForm>;
export type EncounterDetailsFormType = z.infer<typeof EncounterDetailsForm>;
export type EncounterAmendmentInfoValuesType = z.infer<typeof EncounterAmendmentFormValues>;
export type EncounterDiagnosesFormType = z.infer<typeof EncounterDxPlanFormSchema>;

export const getEncounterInitialValues = (
  questionnaireResponse?: QuestionnaireResponse,
  encounter?: Encounter,
  values?: EncounterAmendmentFormType,
): EncounterAmendmentFormType => {
  const initialValues = structuredClone(INITIAL_VALUES);

  if (!questionnaireResponse) return initialValues;

  const answers = getAllQuestionnaireAnswers(questionnaireResponse);

  const getValue = <T>(key: string): T | undefined => {
    if (!answers[key]) return undefined;
    return getQuestionnaireAnswerValue(answers[key]) as T;
  };

  const populateSection = <T extends Record<string, unknown>>(section: T) => {
    Object.keys(section).forEach((key) => {
      const value = getValue<NonNullable<T[keyof T]>>(key);
      if (value !== undefined) {
        section[key as keyof T] = value as T[keyof T];
      }
    });
  };

  if (answers.encounterNote) {
    initialValues.encounterNote = getValue<string>("encounterNote") ?? "";
  }
  if (answers.encounterClientSummary) {
    initialValues.encounterClientSummary = getValue<string>("encounterClientSummary") ?? "";
  }

  if (initialValues.encounterVitalSigns) {
    populateSection<VitalSignsFormType>(initialValues.encounterVitalSigns);
  }
  if (initialValues.encounterFlowSheet) {
    populateSection<PrenatalFlowsheetFormType>(initialValues.encounterFlowSheet);
    if (getValue("encounterFlowSheetDate")) {
      initialValues.encounterFlowSheet.encounterFlowSheetDate = parseDateTimeFormat(
        getValue("encounterFlowSheetDate"),
      );
    }
  }

  if (initialValues.encounterDetails && encounter) {
    const encounterTemplate = encounter.serviceType?.coding?.[0] as Coding;
    const encounterType = encounter?.type?.[0]?.coding?.[0] as Coding;
    const encounterLocation = encounter.class as Coding;

    initialValues.encounterDetails.encounterTemplate = {
      system: encounterTemplate.system as string,
      code: encounterTemplate.code as string,
      display: encounterTemplate.display as string,
    };
    initialValues.encounterDetails.encounterType = {
      system: encounterType.system as string,
      code: encounterType.code as string,
      display: encounterType.display as string,
    };
    initialValues.encounterDetails.encounterLocation = {
      system: encounterLocation.system as Systems.LOINC,
      code: encounterLocation.code as string,
      display: encounterLocation.display as string,
    };

    const serviceDateDetails = getEncounterServiceDateDetailsFromEncounter(encounter);
    initialValues.encounterDetails.encounterStartDate = serviceDateDetails.encounterStartDate;
    initialValues.encounterDetails.encounterStartTime = serviceDateDetails.encounterStartTime;
    initialValues.encounterDetails.encounterStartDateTimezone =
      serviceDateDetails.encounterStartDateTimezone;
  }

  if (initialValues.encounterAmendmentInfo) {
    initialValues.encounterAmendmentInfo.encounterAmendmentInfoNotes =
      values?.encounterAmendmentInfo?.encounterAmendmentInfoNotes;
    initialValues.encounterAmendmentInfo.encounterAmendmentInfoReason =
      (values?.encounterAmendmentInfo?.encounterAmendmentInfoReason as string) ?? "";
    initialValues.encounterAmendmentInfo.encounterAmendmentInfoSpecify =
      values?.encounterAmendmentInfo?.encounterAmendmentInfoSpecify;
    initialValues.encounterAmendmentInfo.encounterAmendmentInfoNotifyClient =
      values?.encounterAmendmentInfo?.encounterAmendmentInfoNotifyClient;
  }

  if (answers.encounterClientDiagnosisCode) {
    initialValues.encounterClientDiagnoses = getEncounterClientDiagnoses(questionnaireResponse);
  }

  return initialValues;
};

/**
 * Extracts encounter client diagnoses from a questionnaire response.
 *
 * This function retrieves the client diagnoses information from a questionnaire response,
 * including the diagnosis code, whether to add it to the problem list, and any associated notes.
 *
 * @param {QuestionnaireResponse} questionnaireResponse - The questionnaire response containing the encounter data.
 *
 * @returns {EncounterDiagnosesFormType[]} An array of encounter client diagnoses.
 */
export function getEncounterClientDiagnoses(questionnaireResponse: QuestionnaireResponse) {
  const encounterClientDiagnoses = questionnaireResponse?.item?.find(
    (item) => item.linkId === "encounterClientDiagnoses",
  );

  if (!encounterClientDiagnoses) {
    return [];
  }

  return encounterClientDiagnoses.item
    ?.map((encounterClientDiagnosis: QuestionnaireResponseItemAnswer) => {
      const diagnosisCode = encounterClientDiagnosis.item?.find(
        (item) => item.linkId === "encounterClientDiagnosisCode",
      )?.answer?.[0]?.valueCoding;

      if (!diagnosisCode) {
        return undefined;
      }

      const addToProblemList = encounterClientDiagnosis.item?.find(
        (item) => item.linkId === "encounterClientDiagnosisAddToProblemList",
      )?.answer?.[0]?.valueBoolean;

      const notes = encounterClientDiagnosis.item?.find(
        (item) => item.linkId === "encounterClientDiagnosisNotes",
      )?.answer?.[0]?.valueString;

      return {
        encounterDiagnosisCode: diagnosisCode,
        encounterDiagnosisAddToProblemList: addToProblemList ?? false,
        encounterDiagnosisNotes: notes ?? "",
      };
    })
    .filter(Boolean) as EncounterDiagnosesFormType[];
}

const IMPERIAL_UNIT_FIELDS = getImperialUnitFields();

export const INITIAL_VALUES: EncounterAmendmentFormType = {
  encounterNote: "",
  encounterClientSummary: "",
  encounterVitalSigns: {
    encounterVitalSignsWeight: undefined,
    encounterVitalSignsWeightUnit: IMPERIAL_UNIT_FIELDS.weightUnit,
    encounterVitalSignsHeightUnit: IMPERIAL_UNIT_FIELDS.heightUnit,
    encounterVitalSignsHeightFeet: undefined,
    encounterVitalSignsHeightInches: undefined,
    encounterVitalSignsHeightCm: undefined,
    encounterVitalSignsSystolicBP: undefined,
    encounterVitalSignsDiastolicBP: undefined,
    encounterVitalSignsHeartRate: undefined,
    encounterVitalSignsTemperature: undefined,
    encounterVitalSignsTemperatureUnit: IMPERIAL_UNIT_FIELDS.temperatureUnit,
    encounterVitalSignsTemperatureMethod: undefined,
    encounterVitalSignsRespiratoryRate: undefined,
    encounterVitalSignsVitalsComments: undefined,
  },
  encounterFlowSheet: {
    encounterFlowSheetWeight: undefined,
    encounterFlowSheetWeightUnit: undefined,
    encounterFlowSheetSystolicBP: undefined,
    encounterFlowSheetDiastolicBP: undefined,
    encounterFlowSheetHeartRate: undefined,
    encounterFlowSheetFundalHeight: undefined,
    encounterFlowSheetFundalHeightUnit: undefined,
    encounterFlowSheetFetalPresentation: undefined,
    encounterFlowSheetFetalHeartRate: undefined,
    encounterFlowSheetFetalMovement: undefined,
    encounterFlowSheetUrineProtein: undefined,
    encounterFlowSheetUrineGlucose: undefined,
    encounterFlowSheetEdema: undefined,
    encounterFlowSheetNotes: undefined,
    encounterFlowSheetDate: undefined,
  },
  encounterAmendmentInfo: {
    encounterAmendmentInfoReason: "",
    encounterAmendmentInfoSpecify: undefined,
    encounterAmendmentInfoNotes: undefined,
    encounterAmendmentInfoNotifyClient: undefined,
  },
  encounterDetails: {
    encounterTemplate: {
      system: Systems.QH,
      code: "",
      display: "",
    },
    encounterLocation: {
      system: Systems.LOINC,
      code: "",
      display: "",
    },
    encounterType: {
      system: Systems.QH,
      code: "",
      display: "",
    },
    ...DEFAULT_ENCOUNTER_SERVICE_DATE_DETAILS,
  },
  encounterClientDiagnoses: [],
};
